import { NavigationBar, useTheme } from '@retailer-platform/shared-components/src/tds'
import { CartIcon } from '@instacart/ids-core'
import { cloneDeep, sortBy } from 'lodash-es'
import { useMemo } from 'react'
import { useStoreConfigurationsByPartnerId } from '../../../api/store-configurations/useStoreConfigurations.hooks'
import { StoreConfigurationRelationshipEnum } from '../../../__codegen__/api'
import { useOptionalPartnerContext } from '../../../utils/contexts/partner/PartnerContext.hooks'
import { PartnerType } from '../../../legacy/common/types'
import usePartnerAccessControl from '../../../legacy/components/AccessControl/usePartnerAccessControl'
import { type RPPNavigationEntry } from '../../../utils/core/RPPCore.types'
import { useCoreContext } from '../../../utils/core/RPPCoreContext'
import {
  classifyStoreConfigSiteTier,
  type SiteTier,
} from '../../../utils/store-config/storeConfigSiteTier'
import { isStoreConfigVisibleToPartner } from '../../../utils/store-config/storeConfigVisibility'
import {
  type NavMenuHierarchyL1,
  type NavMenuHierarchyL2,
  clearEmptyBranches,
  indexRoutes,
  removeInsufficientAccessNavEntries,
} from '../nav-v2/NavMenuHierarchy'
import { NavEntryPointV3 } from './navEntryPointsV3'
import { NavSectionV3, type NavMenuHierarchyV3 } from './navSectionV3'

// v3 sidebar IA — canonical "Nav Pattern Overview" Figma (node 8504:30873).
// 12 L1s grouped under four section headers (Global, Commerce, Operations,
// Developer). Sections are first-class structure: the static hook returns a
// list of { id, l1s } sections, the wrapping hook iterates sections, and the
// renderer renders sections directly.
//
// Each non-Home L1 attaches domain entries directly as leaves via
// `navEntryPointV3`. No inline subsection L2s — the IA flattens v2's
// Analytics-with-subsections shape into Performance + Reports as siblings.
// `hasL2Children` remains unset because there are no intermediate subsections;
// `TdsNavigationBar` renders each attached child as a leaf `NavItem`.
//
// Icons use the TDS NavigationBar icon set (`NavigationBar.Icon name=...`),
// which ships purpose-built glyphs that match the Figma sidebar spec. The
// `caper` L1 falls back to `@instacart/ids-core` `CartIcon` because TDS does
// not expose a caper glyph yet.
const useNavHierarchyRetailerDataV3Static = (): NavMenuHierarchyV3 => {
  const theme = useTheme()
  const { partnerType } = useOptionalPartnerContext() || {}

  return useMemo(() => {
    const home: NavMenuHierarchyL1 = {
      name: 'navV3.folder.home.title',
      icon: () => <NavigationBar.Icon name="home" variant="outline" />,
      // Home is a non-expandable L1: clicking it navigates directly instead of
      // toggling a NavGroupContent panel. The route mirrors `homeRoute` below.
      route:
        partnerType === PartnerType.IDP ? 'instacart-developer-platform-get-started' : 'dashboard',
      children: [],
    }
    const performance: NavMenuHierarchyL1 = {
      name: 'navV3.folder.performance.title',
      icon: () => <NavigationBar.Icon name="analytics" variant="outline" />,
      navEntryPointV3: NavEntryPointV3.Performance,
      children: [],
    }
    const reports: NavMenuHierarchyL1 = {
      name: 'navV3.folder.reports.title',
      icon: () => <NavigationBar.Icon name="reports" variant="outline" />,
      navEntryPointV3: NavEntryPointV3.Reports,
      children: [],
    }
    const catalog: NavMenuHierarchyL1 = {
      name: 'navV2.folder.catalog.title',
      icon: () => <NavigationBar.Icon name="catalog" variant="outline" />,
      navEntryPointV3: NavEntryPointV3.Catalog,
      children: [],
    }
    const marketplace: NavMenuHierarchyL1 = {
      name: 'navV3.folder.marketplace.title',
      icon: () => <NavigationBar.Icon name="marketplace" variant="outline" />,
      navEntryPointV3: NavEntryPointV3.Marketplace,
      children: [],
    }
    const storefront: NavMenuHierarchyL1 = {
      name: 'navV3.folder.storefront.title',
      icon: () => <NavigationBar.Icon name="merchandising" variant="outline" />,
      navEntryPointV3: NavEntryPointV3.Storefront,
      children: [],
    }
    const storefrontPro: NavMenuHierarchyL1 = {
      name: 'navV3.folder.storefrontPro.title',
      icon: () => <NavigationBar.Icon name="merchandising" variant="outline" />,
      navEntryPointV3: NavEntryPointV3.StorefrontPro,
      children: [],
    }
    const customersAndOrders: NavMenuHierarchyL1 = {
      name: 'navV3.folder.customersAndOrders.title',
      icon: () => <NavigationBar.Icon name="customers" variant="outline" />,
      navEntryPointV3: NavEntryPointV3.CustomersAndOrders,
      children: [],
    }
    const stores: NavMenuHierarchyL1 = {
      name: 'navV3.folder.stores.title',
      icon: () => <NavigationBar.Icon name="stores" variant="outline" />,
      navEntryPointV3: NavEntryPointV3.Stores,
      children: [],
    }
    const fulfillment: NavMenuHierarchyL1 = {
      name: 'navV3.folder.fulfillment.title',
      icon: () => <NavigationBar.Icon name="fulfillment" variant="outline" />,
      navEntryPointV3: NavEntryPointV3.Fulfillment,
      children: [],
    }
    const caper: NavMenuHierarchyL1 = {
      name: 'navV3.folder.caper.title',
      icon: CartIcon,
      navEntryPointV3: NavEntryPointV3.Caper,
      children: [],
    }
    const developer: NavMenuHierarchyL1 = {
      name: 'navV2.folder.developer.title',
      icon: () => <NavigationBar.Icon name="developer" variant="outline" />,
      navEntryPointV3: NavEntryPointV3.Developer,
      children: [],
    }

    return {
      navBarColor: theme.colors.brandPrimaryDark,
      navBarColorHover: theme.colors.brandPrimaryRegular,
      type: 'retailer',
      // Home L1 navigates here; matches v2 behaviour for IDP vs. Retailer landing routes.
      homeRoute:
        partnerType === PartnerType.IDP ? 'instacart-developer-platform-get-started' : 'dashboard',
      sections: [
        { id: NavSectionV3.Global, l1s: [home, performance, reports] },
        { id: NavSectionV3.Commerce, l1s: [catalog, marketplace, storefront, storefrontPro] },
        {
          id: NavSectionV3.Operations,
          l1s: [customersAndOrders, stores, fulfillment, caper],
        },
        { id: NavSectionV3.Developer, l1s: [developer] },
      ],
    } satisfies NavMenuHierarchyV3
  }, [theme.colors.brandPrimaryDark, theme.colors.brandPrimaryRegular, partnerType])
}

// Mirrors v2's `attachDomains` shape but reads `navEntryPointV3` from L1/L2 nodes
// and looks up entries in a NavEntryPointV3-keyed map.
const attachDomainsV3 = (
  currentNode: NavMenuHierarchyL1 | NavMenuHierarchyL2,
  entriesByAttachToV3: Partial<Record<NavEntryPointV3, RPPNavigationEntry[]>>
) => {
  currentNode.children?.forEach(child => {
    attachDomainsV3(child, entriesByAttachToV3)
  })

  if (currentNode.navEntryPointV3) {
    const ownEntries = entriesByAttachToV3[currentNode.navEntryPointV3] ?? []
    const newChildren: NavMenuHierarchyL2[] = ownEntries.map(e => ({
      ...e,
      name: e.labelIdV3 ?? e.labelId,
    }))
    currentNode.children = sortBy(
      (currentNode.children || []).concat(newChildren),
      child => child.positionNavV3 ?? 0
    )
  }
}

export const useNavHierarchyRetailerV3 = (doAccessCheck: boolean = true) => {
  const hasAccess = usePartnerAccessControl()
  const { domainNavigationEntriesV3 = {} } = useCoreContext()
  const navMenuHierarchy = useNavHierarchyRetailerDataV3Static()

  const partnerCtx = useOptionalPartnerContext()
  const partnerId = partnerCtx?.partnerId
  const { data: storeConfigsData } = useStoreConfigurationsByPartnerId(
    {
      partnerId: partnerId ?? '',
      storeConfigRelationship: StoreConfigurationRelationshipEnum.Associated,
    },
    { skip: !partnerId }
  )

  // While the query is loading or skipped (no partnerId), this set is empty,
  // so all tier-gated L1s stay hidden until we know which tiers the user can
  // access. Intentionally avoids flashing tier L1s the user may not have.
  const accessibleTiers = useMemo<Set<SiteTier>>(() => {
    const tiers = new Set<SiteTier>()
    storeConfigsData?.storeConfigurationsByPartnerId?.forEach(sc => {
      if (!sc) return
      // The query returns Caper-only and other partner-not-visible configs that
      // the user never sees in the scope picker. Skip those — they would
      // otherwise inflate the tier set (every non-marketplace UIC classifies as
      // 'storefrontPro') and trigger SFP-wins for retailers who have no real
      // SFP access.
      if (!isStoreConfigVisibleToPartner(sc)) return
      tiers.add(
        classifyStoreConfigSiteTier({
          storeConfigurationId: sc.id,
          userIsolationContextId: sc.userIsolationContextId,
        })
      )
    })
    return tiers
  }, [storeConfigsData])

  return useMemo(() => {
    const indexedL1ByRoutes: { [key: string]: NavMenuHierarchyL1 } = {}
    const clonedHierarchy = cloneDeep(navMenuHierarchy)

    // Mutual-exclusion at the L1 layer: Storefront and StorefrontPro share
    // one nav slot. When a user qualifies for both, SFP wins and the SF L1
    // is hidden. Marketplace gates independently on its own tier.
    const showMarketplace = accessibleTiers.has('marketplace')
    const showStorefrontPro = accessibleTiers.has('storefrontPro')
    const showStorefront = !showStorefrontPro && accessibleTiers.has('storefront')

    const TIER_L1_VISIBLE: Partial<Record<NavEntryPointV3, boolean>> = {
      [NavEntryPointV3.Marketplace]: showMarketplace,
      [NavEntryPointV3.Storefront]: showStorefront,
      [NavEntryPointV3.StorefrontPro]: showStorefrontPro,
    }

    clonedHierarchy.sections.forEach(section => {
      // Drop tier-gated L1s the user has no matching store config for, before
      // the leaf-or-route filter below — a tier-gated L1 with kids it can't
      // surface should still get culled by tier first.
      section.l1s = section.l1s.filter(l1 => {
        if (!l1.navEntryPointV3) return true
        const decided = TIER_L1_VISIBLE[l1.navEntryPointV3]
        return decided === undefined ? true : decided
      })

      section.l1s.forEach(l1 => {
        attachDomainsV3(l1, domainNavigationEntriesV3)
        if (doAccessCheck) {
          removeInsufficientAccessNavEntries(l1, hasAccess)
        }
        clearEmptyBranches(l1)
        indexRoutes(l1, l1, indexedL1ByRoutes)
      })
      // Keep L1s that either have surviving children or are leaf L1s (own
      // route/href) — leaf L1s legitimately have no children.
      section.l1s = section.l1s.filter(
        l1 => l1.children.length > 0 || Boolean(l1.route) || Boolean(l1.href)
      )
    })

    clonedHierarchy.sections = clonedHierarchy.sections.filter(s => s.l1s.length > 0)

    return { navMenuHierarchy: clonedHierarchy, indexedL1ByRoutes }
  }, [navMenuHierarchy, domainNavigationEntriesV3, hasAccess, doAccessCheck, accessibleTiers])
}
